/* global axios */
import ApiClient from './ApiClient';

const positiveIntegerPathSegment = (value, name) => {
  const normalizedValue = String(value ?? '').trim();
  if (!/^[1-9]\d*$/.test(normalizedValue)) {
    throw new TypeError(`${name} must be a positive integer`);
  }
  return encodeURIComponent(normalizedValue);
};

class BroadcastsAPI extends ApiClient {
  constructor() {
    super('broadcasts', { accountScoped: true });
  }

  urlForAccount(accountId) {
    if (accountId === undefined || accountId === null) return this.url;
    return `/api/v1/accounts/${positiveIntegerPathSegment(
      accountId,
      'accountId'
    )}/broadcasts`;
  }

  memberUrl(id, { accountId = null } = {}) {
    return `${this.urlForAccount(accountId)}/${positiveIntegerPathSegment(
      id,
      'broadcastId'
    )}`;
  }

  async list({ status = null, q = null, limit = 50, offset = 0 } = {}) {
    const params = { limit, offset };
    if (status) params.status = status;
    if (q) params.q = q;
    return axios.get(this.url, { params });
  }

  async show(id) {
    return axios.get(`${this.url}/${id}`);
  }

  async create(broadcastData) {
    return axios.post(this.url, { broadcast: broadcastData });
  }

  async update(id, broadcastData) {
    return axios.patch(`${this.url}/${id}`, { broadcast: broadcastData });
  }

  async delete(id, options = {}) {
    return axios.delete(this.memberUrl(id, options));
  }

  async duplicate(id, options = {}) {
    return axios.post(`${this.memberUrl(id, options)}/duplicate`);
  }

  async pause(id, options = {}) {
    return axios.post(`${this.memberUrl(id, options)}/pause`);
  }

  async resume(id, options = {}) {
    return axios.post(`${this.memberUrl(id, options)}/resume`);
  }

  async cancel(id, options = {}) {
    return axios.post(`${this.memberUrl(id, options)}/cancel`);
  }

  async retryFailed(id) {
    return axios.post(`${this.url}/${id}/retry_failed`);
  }

  async engagementReport({ since = null, until = null } = {}) {
    const params = {};
    if (since) params.since = since;
    if (until) params.until = until;
    const base = `/api/v2/accounts/${this.accountIdFromRoute}/reports/broadcasts`;
    return axios.get(`${base}/engagement_report`, { params });
  }

  // Download autenticado (axios leva os headers da sessão). `window.open` só
  // mandaria cookie e o endpoint respondia 401 — o botão nunca baixou nada.
  async engagementExport({ since, until, sort } = {}) {
    const base = `/api/v2/accounts/${this.accountIdFromRoute}/reports/broadcasts`;
    return axios.get(`${base}/export.csv`, {
      params: { since, until, sort },
      responseType: 'blob',
    });
  }

  welcomeRulesUrl(inboxId) {
    return `${this.baseUrl()}/inboxes/${inboxId}/broadcast_welcome_rules`;
  }

  async getWelcomeRules(inboxId) {
    return axios.get(this.welcomeRulesUrl(inboxId));
  }

  async createWelcomeRule(inboxId, payload) {
    return axios.post(this.welcomeRulesUrl(inboxId), payload);
  }

  async updateWelcomeRule(inboxId, id, payload) {
    return axios.patch(`${this.welcomeRulesUrl(inboxId)}/${id}`, payload);
  }

  async contacts(id, { status = null, q = null, limit = 50, offset = 0 } = {}) {
    const params = { limit, offset };
    if (status) params.status = status;
    if (q) params.q = q;
    return axios.get(`${this.url}/${id}/contacts`, { params });
  }

  async csvPreview(csvContent) {
    return axios.post(`${this.url}/csv_preview`, { csv_content: csvContent });
  }

  async contactsPreview({ sourceType, sourceConfig }) {
    return axios.post(`${this.url}/contacts_preview`, {
      source_type: sourceType,
      source_config: sourceConfig,
    });
  }

  async exportCsv(id) {
    return axios.get(`${this.url}/${id}/export`, { responseType: 'blob' });
  }
}

export default new BroadcastsAPI();
